import { LogOut } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { stopImpersonation } from '../lib/impersonation'
import { accountHandle } from '../lib/password'
import type { User } from '../lib/types'

export default function ImpersonationBanner({ user }: { user: User }) {
  const { t } = useTranslation()
  const banner = useRef<HTMLDivElement>(null)
  const active = !!user.impersonated_by_id

  // The sentence wraps by language and width, so the bars pinned under it read the height it has now.
  useEffect(() => {
    const el = banner.current
    if (!active || !el) return
    const root = document.documentElement
    const observer = new ResizeObserver(() => {
      root.style.setProperty('--banner-height', `${el.getBoundingClientRect().height}px`)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--banner-height')
    }
  }, [active])

  if (!active) return null

  return (
    <div ref={banner} className="sticky top-0 z-[46] flex flex-wrap items-center justify-between gap-2 bg-amber-500 px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] text-sm font-medium text-amber-950">
      <span>
        {t('admin.viewingAsBanner', { name: user.display_name, email: accountHandle(user) })}
      </span>
      <button
        type="button"
        onClick={() => stopImpersonation()}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-950/10 px-3 py-1 hover:bg-amber-950/20"
      >
        <LogOut className="h-3.5 w-3.5" />
        {t('admin.exitViewAs')}
      </button>
    </div>
  )
}
